import { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { useImpersonationSnapshot } from '@opendoor/auth-fe/impersonation-state';
import { globalObservability } from '@opendoor/observability/slim';
import { impersonationSession } from 'components/account/impersonation';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { getAnonymousId } from 'components/shared/trackers';

import {
  EvaluateListingExperimentDocument,
  EvaluateListingExperimentVariantDocument,
} from './__generated__/listing-experiment.zeus';
import { isUuid } from './useSearchQueries';

const Sentry = globalObservability.getSentryClient();

export type ListingExperimentAssignment = {
  /** Arm this listing resolved to; null when the platform assigned nothing. */
  arm: string | null;
  /**
   * Index GrowthBook assigned, forwarded verbatim from the assignment. Null
   * when the value is not experiment-backed.
   */
  variationId: number | null;
  /** False only while the assignment request is in flight. */
  isResolved: boolean;
};

const UNRESOLVED: ListingExperimentAssignment = { arm: null, variationId: null, isResolved: false };
const NOT_ENROLLED: ListingExperimentAssignment = {
  arm: null,
  variationId: null,
  isResolved: true,
};

type UseListingExperimentOptions = {
  experimentKey: string;
  /** Listing the arm is bucketed on, and the join key every readout uses. */
  addressId: string;
  /** Whether this listing is eligible. An ineligible listing is never bucketed. */
  enabled: boolean;
};

/**
 * One assignment per (experiment, listing) per page session.
 *
 * Identity is deliberately NOT part of the key. The listing is the unit of
 * randomisation, so a viewer who signs in with the PDP open is the same unit
 * looking at the same listing, not a new one: re-evaluating would return the
 * arm already on screen and bill a second exposure for one listing view, which
 * inflates the denominator the readout divides by. Identity still travels in
 * the grain, so the exposure attaches to whoever was present at first paint.
 */
const assignments = new Map<string, Promise<ListingExperimentAssignment>>();

/** Exported for tests; the cache is otherwise page-session lifetime. */
export const __resetListingExperimentCache = () => assignments.clear();

type Identity = { customerId?: string; anonymousId?: string };

const assignmentKey = (experimentKey: string, addressId: string) => `${experimentKey}|${addressId}`;

/** An assignment together with the listing it was requested for. */
type ResolvedAssignment = { key: string; assignment: ListingExperimentAssignment };

const usableIdentity = (
  customerId: string | null,
  anonymousId: string | undefined,
): Identity | null => {
  const identity: Identity = {
    ...(customerId ? { customerId } : {}),
    // Segment's cookie is not guaranteed to hold a UUID, and a malformed one
    // fails the whole mutation rather than degrading to the customer id.
    ...(anonymousId && isUuid(anonymousId) ? { anonymousId } : {}),
  };
  return (identity.customerId ?? identity.anonymousId) ? identity : null;
};

const evaluate = async (
  experimentKey: string,
  addressId: string,
  identity: Identity,
): Promise<ListingExperimentAssignment> => {
  const result = await requestZeusFromBrowser(EvaluateListingExperimentVariantDocument, {
    input: {
      // The caller records the exposure, because Variant derives an
      // auto-impression from the grain and no grain Zeus exposes puts a listing
      // on one. That row carries a null `address_token`, and the
      // property-grained assignment query this experiment reads drops it.
      autoImpression: false,
      grain: { customer: identity },
      key: experimentKey,
      // Bucketing attribute. `address_token` is the registered GrowthBook
      // hash attribute for a property, and the rule must name it: an
      // unregistered key buckets on viewer identity instead, which splits a
      // single listing across both arms and destroys the listing-level
      // randomisation this experiment depends on. It carries the same value as
      // the `address_uuid` on the Journey events, despite the different name.
      props: { address_token: addressId },
    },
  });

  const payload = result.evaluateExperimentVariant;
  if (payload.__typename !== 'EvaluateExperimentVariantPayload') {
    return NOT_ENROLLED;
  }

  return {
    arm: payload.assignment.value,
    variationId: payload.assignment.variationId,
    isResolved: true,
  };
};

const evaluateForSupport = async (
  experimentKey: string,
  addressId: string,
  customerId: string,
): Promise<ListingExperimentAssignment> => {
  const result = await requestZeusFromBrowser(EvaluateListingExperimentDocument, {
    input: {
      grain: { customer: { customerId } },
      keys: [experimentKey],
    },
    addressId,
  });
  const payload = result.experiments.evaluations;
  if (payload?.__typename !== 'ExperimentsQueryEvaluationsSuccess') {
    Sentry.captureException?.(
      new Error(
        payload
          ? `Experiment evaluation rejected for ${experimentKey}: ${payload.code} ${payload.message}`
          : `Experiment evaluation returned no result for ${experimentKey}`,
      ),
    );
    return NOT_ENROLLED;
  }

  const assignment = payload.data.find((candidate) => candidate.key === experimentKey);
  return assignment
    ? { arm: assignment.value, variationId: assignment.variationId, isResolved: true }
    : NOT_ENROLLED;
};

/**
 * Assigns a listing-bucketed experiment arm for one listing.
 *
 * Randomisation is on the listing, not the viewer: every viewer of a listing
 * sees the same arm, which is what keeps a media change attributable to the
 * listing rather than to whoever happened to open it.
 */
export const useListingExperiment = ({
  experimentKey,
  addressId,
  enabled,
}: UseListingExperimentOptions): ListingExperimentAssignment => {
  const { authentication } = useAuth();
  const { customerUUID: supportCustomerUUID } = useImpersonationSnapshot(impersonationSession);
  const [resolved, setResolved] = useState<ResolvedAssignment | null>(null);
  // Segment writes `ajs_anonymous_id` from the browser, so it is read after
  // mount rather than during a render that also runs on the server.
  const [anonymousId, setAnonymousId] = useState<string>();
  const [hasReadBrowser, setHasReadBrowser] = useState(false);
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setAnonymousId(getAnonymousId());
    setHasReadBrowser(true);
  }, []);

  // Allowlisted rather than "not authenticating": the context's fourth state,
  // `error`, means the session could not be determined, so a signed-in viewer
  // may be behind it. Bucketing there records the exposure against an anonymous
  // id that a recovering session would replace.
  const isAuthSettled =
    authentication.state === 'authenticated' || authentication.state === 'not_authenticated';
  const customerId =
    authentication.state === 'authenticated' ? (authentication.user.customerUuid ?? null) : null;
  const key = assignmentKey(experimentKey, addressId);

  useEffect(() => {
    if (!enabled || !isAuthSettled || !hasReadBrowser) {
      return undefined;
    }

    let active = true;
    let pending = assignments.get(key);
    if (!pending) {
      if (supportCustomerUUID) {
        pending = evaluateForSupport(experimentKey, addressId, supportCustomerUUID);
      } else {
        const identity = usableIdentity(customerId, anonymousId);
        if (!identity) {
          // Nothing to bucket on. Recorded as unenrolled for this render but never
          // written to the cache, so an identity that shows up later assigns
          // instead of leaving the listing unenrolled for the whole session.
          // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
          setResolved({ key, assignment: NOT_ENROLLED });
          return undefined;
        }
        pending = evaluate(experimentKey, addressId, identity);
      }

      pending = pending.catch((error: unknown) => {
        // Evict so a transient failure is not cached as "not enrolled" for the
        // rest of the session.
        assignments.delete(key);
        Sentry.captureException?.(error);
        return NOT_ENROLLED;
      });
      assignments.set(key, pending);
    }

    void pending.then((assignment) => {
      if (active) {
        setResolved({ key, assignment });
      }
      return assignment;
    });

    return () => {
      active = false;
    };
  }, [
    addressId,
    anonymousId,
    customerId,
    enabled,
    experimentKey,
    hasReadBrowser,
    isAuthSettled,
    key,
    supportCustomerUUID,
  ]);

  if (!enabled) {
    return NOT_ENROLLED;
  }
  // An assignment belongs to the listing it was requested for. The PDP swaps
  // the listing under a mounted component, so returning the previous listing's
  // arm while this one is still in flight would tag its exposure with the wrong
  // arm — and the reader that fires on first non-null arm never corrects itself.
  if (resolved?.key !== key) {
    return UNRESOLVED;
  }
  return resolved.assignment;
};
